import MuiTooltip from '@mui/material/Tooltip';
// eslint-disable-next-line no-restricted-imports
import type { TooltipProps as MuiTooltipProps } from '@mui/material/Tooltip';
import { linkClasses } from '@mui/material/Link';
import type { Theme } from '@mui/material/styles';
import type { SxProps } from '@mui/system';
import {
  tooltipDarkSlotProps,
  tooltipLightSlotProps,
} from '@watershed/style/tooltips';
import mixinSx from '@watershed/style/mixinSx';

export type TooltipTheme = 'light' | 'dark';

/**
 * Removes deprecated props from TooltipProps to ensure we don't use them
 */
export type TooltipProps = Omit<
  MuiTooltipProps,
  | 'components'
  | 'componentsProps'
  | 'PopperComponent'
  | 'TransitionComponent'
  | 'PopperProps'
  | 'TransitionProps'
> & {
  theme?: 'light' | 'dark';
  /**
   * Convenience styling hook for tooltip content styles.
   *
   * This is applied to `slotProps.tooltip.sx`.
   */
  sx?: SxProps<Theme>;
};

const darkLinkSx: SxProps<Theme> = {
  [`& .${linkClasses.root}`]: {
    color: (theme) => theme.palette.cobalt30,
  },
};

export default function Tooltip({
  theme = 'dark',
  sx,
  slotProps,
  ...props
}: TooltipProps): JSX.Element {
  const themed =
    theme === 'light' ? tooltipLightSlotProps() : tooltipDarkSlotProps();

  const themedPopper = themed.slotProps.popper;
  const themedTooltip = themed.slotProps.tooltip;

  // Function slotProps are not supported; only object slotProps are merged.
  const userPopper =
    slotProps?.popper != null && typeof slotProps.popper === 'object'
      ? slotProps.popper
      : undefined;
  const userTooltip =
    slotProps?.tooltip != null && typeof slotProps.tooltip === 'object'
      ? slotProps.tooltip
      : undefined;
  const linkSx = theme === 'dark' ? darkLinkSx : undefined;

  return (
    <MuiTooltip
      {...props}
      arrow={false}
      slotProps={{
        ...themed.slotProps,
        ...slotProps,
        popper: {
          ...themedPopper,
          ...userPopper,
          modifiers: userPopper?.modifiers ?? themedPopper.modifiers,
        },
        tooltip: {
          ...themedTooltip,
          ...userTooltip,
          sx: mixinSx(themedTooltip?.sx, linkSx, userTooltip?.sx, sx),
        },
      }}
    />
  );
}
